import React, { Dispatch, SetStateAction } from "react";
import styled from "@emotion/styled";
import { Drawer } from "antd";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import BasicButton from "@components/button/BasicButton";
import { useMutation } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { queryClient } from "@libs/reactQuery/queryClient";

const DrawerContainer = styled(Drawer)(({ theme }) => ({
  borderRadius: "10px 10px 0 0",
  fontFamily: "Pretendard ,Sans-serif",

  ".ant-drawer-header": {
    display: "flex",
    borderBottom: "none",
  },

  ".ant-drawer-title": {
    display: "flex",
    justifyContent: "flex-end",
  },

  ".ant-drawer-body": {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "0 2rem 4.8rem",
  },
}));

const ContentWrapper = styled.div(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "space-between",
  gap: "1.6rem",
  color: theme.color.black,
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
}));

const Title = styled.span(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.bold,
  color: theme.color.black,
}));

const InfoBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "1.6rem",
  color: theme.color.gray,
  fontWeight: theme.fontWeight.regular,
  lineHeight: "2.4rem",
  paddingTop: "4rem",

  ".title": {
    color: theme.color.green,
    fontWeight: theme.fontWeight.bold,
  },
}));

const DescriptionUl = styled.ul(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.regular,
}));

const ButtonWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSuccessModal: Dispatch<SetStateAction<boolean>>;
};
const CancelSubscribeDrawer = ({ open, setOpen, setSuccessModal }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { patchMemberSubscription } = useApiRequest();

  const { mutate } = useMutation({
    mutationFn: () => patchMemberSubscription(),
    onSuccess: async () => {
      setOpen(false);
      setSuccessModal(true);
      await queryClient.invalidateQueries({ queryKey: ["subscribeDetail"] });
    },
  });

  const handleCancelSubscribe = () => {
    mutate();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <DrawerContainer
      title={
        <button onClick={handleClose}>
          <Image src={theme.image.close.default} />
        </button>
      }
      closable={false}
      height={"70dvh"}
      placement="bottom"
      onClose={handleClose}
      open={open}>
      <ContentWrapper>
        <div>
          <Title>{t("구독을 취소하시겠습니까?")}</Title>

          <InfoBox>
            <span className="title">{t("구독 취소 안내")}</span>

            <DescriptionUl>
              <li>
                • 구독을 취소 하더라도 기존에 유료 결제한 멤버쉽 기간은 그대로
                유지되며, 이미 결제된 금액은 환불되지 않습니다
              </li>
              <li>
                • 멤버쉽 혜택이 한번도 사용된 적이 없다면, 결제일로부터 7일
                이내에 [직접 결제 취소]하실 수 있습니다.
              </li>
              <li>
                • 샤카라카 서비스 이 외, 타사 공급자를 통해 멤버쉽을 구입한 경우
                공급자의 환불 정책이 적용됩니다.
              </li>
              <li>
                • Apple App Store 구매의 경우, [Apple 지원팀]에 환불을
                요청하세요.
              </li>
            </DescriptionUl>
          </InfoBox>
        </div>

        <ButtonWrapper>
          <BasicButton title="네" onClick={handleCancelSubscribe} />
          <BasicButton title="아니요" noBorder outlined onClick={handleClose} />
        </ButtonWrapper>
      </ContentWrapper>
    </DrawerContainer>
  );
};

export default CancelSubscribeDrawer;
